import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';

// Tab components
import ExploreTab from '../components/cannabis/ExploreTab';
import StrainsTab from '../components/cannabis/StrainsTab';
import HashTab from '../components/cannabis/HashTab';
import LawsTab from '../components/cannabis/LawsTab';
import DispensariesTab from '../components/cannabis/DispensariesTab';
import GuidesTab from '../components/cannabis/GuidesTab';
import TourismTab from '../components/cannabis/TourismTab';
import StrainQuizTab from '../components/cannabis/StrainQuizTab';
import DosageCalcTab from '../components/cannabis/DosageCalcTab';
import StrainCompareTab from '../components/cannabis/StrainCompareTab';
import GlossaryTab from '../components/cannabis/GlossaryTab';
import TripPlannerTab from '../components/cannabis/TripPlannerTab';
import TerpeneWheelTab from '../components/cannabis/TerpeneWheelTab';
import WorldMapTab from '../components/cannabis/WorldMapTab';
import PanicButtonTab from '../components/cannabis/PanicButtonTab';
import EdibleTimerTab from '../components/cannabis/EdibleTimerTab';
import CountdownTab from '../components/cannabis/CountdownTab';
import MunchiesTab from '../components/cannabis/MunchiesTab';
import MusicPairingTab from '../components/cannabis/MusicPairingTab';
import MethodFinderTab from '../components/cannabis/MethodFinderTab';

export interface CountryProps {
  userCountry?: { name: string; code: string } | null;
  isGlobal: boolean;
}

/* ── Category structure ── */
interface TabDef {
  key: string;
  label: string;
  component: React.FC<CountryProps>;
}

interface Category {
  key: string;
  label: string;
  tabs: TabDef[];
}

const CATEGORIES: Category[] = [
  {
    key: 'discover', label: 'Discover',
    tabs: [
      { key: 'explore', label: 'Explore', component: ExploreTab },
      { key: 'map', label: 'World Map', component: WorldMapTab },
      { key: 'travel', label: 'Travel', component: TripPlannerTab },
      { key: 'tourism', label: 'Destinations', component: TourismTab },
      { key: 'laws', label: 'Laws', component: LawsTab },
      { key: 'dispensaries', label: 'Dispensaries', component: DispensariesTab },
    ],
  },
  {
    key: 'strains', label: 'Strains',
    tabs: [
      { key: 'browse', label: 'Flower', component: StrainsTab },
      { key: 'hash', label: 'Hash', component: HashTab },
      { key: 'compare', label: 'Compare', component: StrainCompareTab },
      { key: 'quiz', label: 'Find Your Strain', component: StrainQuizTab },
      { key: 'terpenes', label: 'Terpenes', component: TerpeneWheelTab },
    ],
  },
  {
    key: 'tools', label: 'Tools',
    tabs: [
      { key: 'calculators', label: 'Calculators', component: DosageCalcTab },
      { key: 'method', label: 'Find My Method', component: MethodFinderTab },
      { key: 'edibletimer', label: 'Edible Timer', component: EdibleTimerTab },
      { key: 'panic', label: 'Too High?', component: PanicButtonTab },
      { key: 'glossary', label: 'Glossary', component: GlossaryTab },
    ],
  },
  {
    key: 'learn', label: 'Learn',
    tabs: [
      { key: 'guides', label: 'All Guides', component: GuidesTab },
    ],
  },
  {
    key: 'vibes', label: 'Vibes',
    tabs: [
      { key: 'music', label: 'Music Pairing', component: MusicPairingTab },
      { key: 'munchies', label: 'Munchies', component: MunchiesTab },
      { key: '420', label: '420 Countdown', component: CountdownTab },
    ],
  },
];

export default function Cannabis() {
  const [activeCategory, setActiveCategory] = useState('discover');
  const [activeSubTab, setActiveSubTab] = useState('explore');

  const category = CATEGORIES.find(c => c.key === activeCategory)!;
  const tab = category.tabs.find(t => t.key === activeSubTab) || category.tabs[0];
  const ActiveComponent = tab.component;

  function switchCategory(catKey: string) {
    setActiveCategory(catKey);
    const cat = CATEGORIES.find(c => c.key === catKey)!;
    setActiveSubTab(cat.tabs[0].key);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-5">
      {/* Main Category Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {CATEGORIES.map(cat => (
          <button
            key={cat.key}
            onClick={() => switchCategory(cat.key)}
            className={`relative px-5 py-2.5 text-sm font-semibold rounded-xl whitespace-nowrap transition-colors ${
              activeCategory === cat.key
                ? 'text-[#4ade80]'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            {cat.label}
            {activeCategory === cat.key && (
              <motion.div
                layoutId="category-indicator"
                className="absolute inset-0 bg-[#4ade80]/10 border border-[#4ade80]/20 rounded-xl -z-10"
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Sub-tabs (only if category has more than 1 tab) */}
      {category.tabs.length > 1 && (
        <div className="flex gap-1 overflow-x-auto scrollbar-hide">
          {category.tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveSubTab(t.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
                activeSubTab === t.key
                  ? 'bg-[#d4a574]/20 text-[#d4a574] border border-[#d4a574]/30'
                  : 'text-white/35 hover:text-white/55 border border-transparent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Active Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${activeCategory}-${activeSubTab}`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          <ActiveComponent userCountry={null} isGlobal={true} />
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
